import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box } from '@mui/material'

/**
 * Generic Empty State Component
 *
 * Replaces all the duplicate empty state components (EmptyPortfolio, EmptyMarketplace, EmptyLots, EmptyTrash)
 * with a single, configurable component.
 *
 * @param {Object} props
 * @param {Array} props.icon - FontAwesome icon array [style, iconName]
 * @param {string} props.title - Main title text
 * @param {string} props.description - Description text below title
 * @param {React.ReactNode} props.actionButton - Optional action button component
 * @param {boolean} props.hide - Whether to hide the component
 * @param {string} props.iconBackgroundColor - Custom icon background color (default: #c1ebcf)
 * @param {string} props.iconColor - Custom icon color (default: #2ab755)
 * @param {Object} props.style - Additional styles for the container
 * @param {string} props.className - Additional CSS classes
 */
const EmptyState = ({
  icon = ['far', 'folder-open'],
  title,
  description,
  actionButton,
  hide = false,
  iconBackgroundColor = '#c1ebcf',
  iconColor = '#2ab755',
  style = {},
  className = '',
  ...props
}) => {
  if (hide) {
    return null
  }

  return (
    <Box
      className={className}
      style={style}
      sx={{ textAlign: 'center', p: '3rem' }}
      {...props}
    >
      <Box sx={[{ borderRadius: '50%', m: 0 }, {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        mr: "0.25rem",
        position: "relative",
        fontSize: "1.2rem",
        transition: "all 0.2s ease-in-out",
        "&:last-child": { mr: 0, marginRight: 0 },
        "& .badge": { position: "absolute", right: 0, bottom: 0 },
        "& svg, & .svg-inline--fa": { display: "block" },
        marginRight: "0.25rem",
      }]}>
        <Box style={{ backgroundColor: iconBackgroundColor, color: iconColor, }} sx={[{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          border: 0,
          height: "130px !important",
          width: "130px !important",
          lineHeight: "130px !important",
          p: 0,
          borderRadius: "50%",
          m: 0,
        }, {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          mr: "0.25rem",
          position: "relative",
          fontSize: "1.2rem",
          transition: "all 0.2s ease-in-out",
          "&:last-child": { mr: 0, marginRight: 0 },
          "& .badge": { position: "absolute", right: 0, bottom: 0 },
          "& svg, & .svg-inline--fa": { display: "block" },
          marginRight: "0.25rem",
        }]}>
          <FontAwesomeIcon
            icon={icon}
            style={{ display: 'block', fontSize: '2.5rem', lineHeight: 1 }}
          />
        </Box>
      </Box>

      <Box component="h4" sx={{ fontWeight: 700, mt: '1.5rem' }}>
        {title}
      </Box>

      {description && (
        <Box component="p" sx={[{ mb: 0, color: '#a6a6b9' }, { fontSize: "1.15rem !important" }]}>
          {description}
        </Box>
      )}

      {actionButton && (
        <Box sx={{ pt: '1.5rem' }}>{actionButton}</Box>
      )}
    </Box>
  )
}

export default EmptyState
